'use client'
import Link from 'next/link'
import { useNavUI } from '@/lib/navContext'

export default function MobileBottomNav() {
  const { openSidebar, openSearch } = useNavUI()

  // Open via context (core, same tree) and a window event (casino, where this bar is mounted
  // in a separate widget root that can't share this context) — same idempotent pattern as
  // SidebarTrigger and NavSearch's own cross-root listener.
  function handleOpenSearch() {
    openSearch()
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('weeabets:open-search'))
  }
  function handleOpenSidebar() {
    openSidebar()
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('weeabets:open-sidebar'))
  }

  return (
    <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 flex items-center justify-around px-2 py-2 safe-area-inset-bottom">
      <Link
        href="/"
        className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
        </svg>
        <span className="text-[10px] font-medium">Home</span>
      </Link>

      <button
        onClick={handleOpenSearch}
        className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
        </svg>
        <span className="text-[10px] font-medium">Search</span>
      </button>

      <Link
        href="/casino"
        className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <rect x="2" y="2" width="16" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="2"/>
          <circle cx="6.5" cy="6.5" r="1.25"/>
          <circle cx="13.5" cy="6.5" r="1.25"/>
          <circle cx="6.5" cy="13.5" r="1.25"/>
          <circle cx="13.5" cy="13.5" r="1.25"/>
          <circle cx="10" cy="10" r="1.25"/>
        </svg>
        <span className="text-[10px] font-medium">Casino</span>
      </Link>

      <Link
        href="/fantasy-dungeon-league"
        className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <circle cx="12" cy="6" r="2"></circle>
          <path d="M1 18a1 1 0 0 1-.707-1.707l7-7a1 1 0 0 1 1.414 1.414l-7 7A1 1 0 0 1 1 18zM15 4a1 1 0 0 1-.707-1.707l2-2a1 1 0 0 1 1.414 1.414l-2 2A1 1 0 0 1 15 4zm-3-1a1 1 0 0 1-1-1V1a1 1 0 0 1 2 0v1a1 1 0 0 1-1 1zm0 9a1 1 0 0 1-1-1v-1a1 1 0 0 1 2 0v1a1 1 0 0 1-1 1z"></path>
          <path d="M12 3a1 1 0 0 1-1-1V1a1 1 0 0 1 2 0v1a1 1 0 0 1-1 1zm0 9a1 1 0 0 1-1-1v-1a1 1 0 0 1 2 0v1a1 1 0 0 1-1 1zM8 7H7a1 1 0 1 1 0-2h1a1 1 0 1 1 0 2zm9 0h-1a1 1 0 0 1 0-2h1a1 1 0 0 1 0 2zM9 4a1 1 0 0 1-.707-.293l-2-2A1 1 0 0 1 7.707.293l2 2A1 1 0 0 1 9 4zm8 8a1 1 0 0 1-.707-.293l-2-2a1 1 0 0 1 1.414-1.414l2 2A1 1 0 0 1 17 12z"></path>
        </svg>
        <span className="text-[10px] font-medium">FDL</span>
      </Link>

      <Link
        href="/risk-japan"
        className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <ellipse cx="15" cy="3" rx="1.6" ry="1" transform="rotate(75 15 3)" />
          <ellipse cx="15.4" cy="8.6" rx="2.4" ry="1.3" transform="rotate(95 15.4 8.6)" />
          <ellipse cx="14.1" cy="12.9" rx="2.8" ry="1.5" transform="rotate(125 14.1 12.9)" />
          <ellipse cx="10.4" cy="15.6" rx="2.2" ry="1.2" transform="rotate(150 10.4 15.6)" />
          <ellipse cx="5" cy="17" rx="1.3" ry="0.8" transform="rotate(165 5 17)" />
        </svg>
        <span className="text-[10px] font-medium">Risk</span>
      </Link>

      <button
        onClick={handleOpenSidebar}
        className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M3 6a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
        <span className="text-[10px] font-medium">More</span>
      </button>
    </nav>
  )
}
